import React, { useState, useEffect } from 'react';
import { MaintenanceHistory } from '../components/maintenance/MaintenanceHistory';
import { MaintenanceNotifications } from '../components/maintenance/MaintenanceNotifications';
import { MaintenanceHistoryEntry, MaintenanceSession } from '../types/maintenance';
import { api } from '../utils/api';

interface MachineStats {
  machineId: string;
  machineCode: string;
  location: string;
  totalSessions: number;
  totalDuration: number;
  averageDuration: number;
  daysSinceLastMaintenance: number;
}

interface ActiveSession {
  id: string;
  machineId: string;
  machineCode: string;
  employeeId: string;
  employeeCode: string;
  employeeName?: string;
  startTime: string;
}

export const MaintenanceReportsPage: React.FC = () => {
  const [history, setHistory] = useState<MaintenanceHistoryEntry[]>([]);
  const [longRunningSessions, setLongRunningSessions] = useState<MaintenanceSession[]>([]);
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);
  const [machineStats, setMachineStats] = useState<MachineStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [thresholdMinutes, setThresholdMinutes] = useState(60);
  const [maintenanceThresholdDays, setMaintenanceThresholdDays] = useState(7);
  const [forceEndingId, setForceEndingId] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch all maintenance history
      const historyResponse = await api.get<MaintenanceHistoryEntry[]>(
        '/maintenance-sessions/history'
      );
      setHistory(historyResponse.data);

      // Fetch long-running sessions
      const longRunningResponse = await api.get<MaintenanceSession[]>(
        `/maintenance-sessions/long-running?thresholdMinutes=${thresholdMinutes}`
      );
      setLongRunningSessions(longRunningResponse.data);

      // Fetch active sessions (for force-end UI)
      const activeResponse = await api.get<ActiveSession[]>('/maintenance-sessions/active');
      setActiveSessions(Array.isArray(activeResponse.data) ? activeResponse.data : []);

      // Calculate machine statistics from history
      calculateMachineStats(historyResponse.data);
    } catch (err) {
      console.error('Error fetching maintenance data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load maintenance data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForceEnd = async (sessionId: string, machineCode: string) => {
    const reason = window.prompt(
      `Encerrar sessão da máquina ${machineCode} à força?\n\nMotivo (opcional, será registrado no histórico):`,
      ''
    );

    // window.prompt returns null if user cancels (vs empty string for confirmed-but-no-reason)
    if (reason === null) return;

    setForceEndingId(sessionId);
    try {
      await api.post(`/maintenance-sessions/${sessionId}/force-end`, {
        reason: reason.trim() || undefined
      });
      await fetchData();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Erro desconhecido';
      window.alert(`Falha ao encerrar sessão: ${msg}`);
    } finally {
      setForceEndingId(null);
    }
  };

  const calculateMachineStats = (historyData: MaintenanceHistoryEntry[]) => {
    const machineMap = new Map<string, MachineStats>();

    historyData.forEach((entry) => {
      const { machine, session } = entry;
      
      if (!machineMap.has(machine.id)) {
        machineMap.set(machine.id, {
          machineId: machine.id,
          machineCode: machine.code,
          location: machine.location,
          totalSessions: 0,
          totalDuration: 0,
          averageDuration: 0,
          daysSinceLastMaintenance: 0,
        });
      }

      const stats = machineMap.get(machine.id)!;
      stats.totalSessions++;
      stats.totalDuration += session.duration || 0;
    });

    // Calculate averages and days since last maintenance
    const now = new Date();
    machineMap.forEach((stats) => {
      if (stats.totalSessions > 0) {
        stats.averageDuration = Math.round(stats.totalDuration / stats.totalSessions);
      }

      // Find most recent session for this machine
      const machineSessions = historyData
        .filter((entry) => entry.machine.id === stats.machineId)
        .sort((a, b) => 
          new Date(b.session.startTime).getTime() - new Date(a.session.startTime).getTime()
        );

      if (machineSessions.length > 0) {
        const lastSession = machineSessions[0];
        const lastMaintenanceDate = new Date(lastSession.session.startTime);
        const diffTime = Math.abs(now.getTime() - lastMaintenanceDate.getTime());
        stats.daysSinceLastMaintenance = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      }
    });

    setMachineStats(Array.from(machineMap.values()));
  };

  const handleExportCSV = () => {
    if (history.length === 0) return;

    // Create CSV content
    const headers = [
      'Código Funcionário',
      'Nome Funcionário',
      'Código Máquina',
      'Local',
      'Data Início',
      'Hora Início',
      'Data Fim',
      'Hora Fim',
      'Duração (minutos)',
      'Observações'
    ];

    const rows = history.map((entry) => {
      const startDate = new Date(entry.session.startTime);
      const endDate = entry.session.endTime ? new Date(entry.session.endTime) : null;

      return [
        entry.employee.employeeCode,
        entry.employee.name,
        entry.machine.code,
        entry.machine.location,
        startDate.toLocaleDateString('pt-BR'),
        startDate.toLocaleTimeString('pt-BR'),
        endDate ? endDate.toLocaleDateString('pt-BR') : '',
        endDate ? endDate.toLocaleTimeString('pt-BR') : '',
        entry.session.duration || '',
        entry.session.notes || ''
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `historico-manutencao-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get machines needing maintenance (overdue)
  const machinesNeedingMaintenance = machineStats.filter(
    (stats) => stats.daysSinceLastMaintenance > maintenanceThresholdDays
  );

  // Calculate overall statistics
  const totalSessions = history.length;
  const totalDuration = history.reduce((sum, entry) => sum + (entry.session.duration || 0), 0);
  const averageDuration = totalSessions > 0 ? Math.round(totalDuration / totalSessions) : 0;

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-6 h-6 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-red-800 font-semibold">Erro ao carregar dados</h3>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Relatórios de Manutenção</h1>
        <p className="text-gray-600">
          Visualize estatísticas e histórico completo de manutenções
        </p>
      </div>

      {/* Overall Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total de Sessões</p>
              <p className="text-3xl font-bold text-orange-600">{totalSessions}</p>
            </div>
            <div className="bg-orange-100 rounded-full p-3">
              <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Duração Média</p>
              <p className="text-3xl font-bold text-blue-600">{formatDuration(averageDuration)}</p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Máquinas Precisando Manutenção</p>
              <p className="text-3xl font-bold text-red-600">{machinesNeedingMaintenance.length}</p>
            </div>
            <div className="bg-red-100 rounded-full p-3">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Long-Running Sessions Notification Panel */}
      <div className="mb-8">
        <MaintenanceNotifications onSettingsUpdate={fetchData} />
      </div>

      {/* Active Sessions (with force-end) */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Sessões de Manutenção Ativas</h2>
          <p className="text-sm text-gray-600 mt-1">
            Sessões em andamento. Use "Encerrar à força" se um funcionário esqueceu de finalizar e a máquina ficou presa em manutenção.
          </p>
        </div>
        <div className="p-6">
          {activeSessions.length === 0 ? (
            <p className="text-sm text-gray-500">Nenhuma sessão de manutenção ativa no momento.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Máquina</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Funcionário</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Início</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tempo decorrido</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {activeSessions.map((s) => {
                    const elapsedMin = Math.round((Date.now() - new Date(s.startTime).getTime()) / 60000);
                    return (
                      <tr key={s.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-mono font-semibold text-gray-900">{s.machineCode}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {s.employeeName || s.employeeCode}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {formatDateTime(s.startTime)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                            elapsedMin > thresholdMinutes
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {formatDuration(elapsedMin)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleForceEnd(s.id, s.machineCode)}
                            disabled={forceEndingId === s.id}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed transition-colors"
                          >
                            {forceEndingId === s.id ? 'Encerrando...' : 'Encerrar à força'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Machines Needing Maintenance */}
      {machinesNeedingMaintenance.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Máquinas Precisando Manutenção
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Máquinas que não recebem manutenção há mais de {maintenanceThresholdDays} dias
            </p>
          </div>
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Código
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Local
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Total Sessões
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Dias Desde Última Manutenção
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {machinesNeedingMaintenance.map((stats) => (
                    <tr key={stats.machineId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-mono font-semibold text-gray-900">
                          {stats.machineCode}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {stats.location}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {stats.totalSessions}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-red-100 text-red-800">
                          {stats.daysSinceLastMaintenance} dias
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Maintenance History */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Histórico de Manutenções</h2>
        </div>
        <div className="p-6">
          <MaintenanceHistory
            history={history}
            onExportCSV={handleExportCSV}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};
